import { Button, Form } from 'antd';
import React, { FC, useEffect, useMemo } from 'react';
import { $Formutil, withForm } from 'react-antd-formutil';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BankCardSelect from 'components/BankCardSelect';
import BottomBox from 'components/BottomBox';
import CurrencyAmount from 'components/CurrencyAmount';
import Debug from 'components/Debug';
import ErrorBox from 'components/ErrorBox';
import UploaderList from 'components/FileUploader/UploaderList';
import Loading from 'components/Loading';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import { useGetBankCards } from 'modules/BankCards/request';
import TaxLimit from 'modules/Deposit/components/TaxLimit';
import API from 'utils/API';
import http from 'utils/http';
import { getCurrencyInfo } from 'utils/util';
import { transferDepositCurrencyList } from '../helper';
import { useChoiceList } from '../request';
import { AccDataTypes, parseNoticeInfo } from './helper';
import './style.scss';

interface FillNoticeProps {
    isIRA?: boolean;
    type?: 'wire' | 'check';
    $formutil: $Formutil<{
        amount: string;
        currency: string;
        bank_account: string;
        tax_year: string;
        tax_limit: number;
    }>;
}

const FillNotice: FC<FillNoticeProps> = ({ $formutil, isIRA, type = 'wire' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as
        | (AccDataTypes & {
              currency: string;
          })
        | null;
    const { $invalid, $batchState, $params, $getFirstError } = $formutil;
    const {
        isMutating: isMutatingBankCard,
        error: errorBankCard,
        data: dataBankCard,
        trigger: triggerBankCard
    } = useGetBankCards({
        queryType: 'manage'
    });

    const {
        isLoading: isLoadingChoiceList,
        error: errorChoiceList,
        data: dataChoiceList,
        mutate: mutateChoiceList
    } = useChoiceList();

    const currencyList = useMemo(() => {
        if (dataChoiceList) {
            const list = Object.keys(dataChoiceList);

            return list.map(item => {
                const currencyInfo = getCurrencyInfo(item);

                return {
                    value: item,
                    label: currencyInfo.name,
                    icon: currencyInfo.icon,
                    key: currencyInfo.name
                };
            });
        }

        return [];
    }, [dataChoiceList]);

    useEffect(() => {
        triggerBankCard();
    }, [triggerBankCard]);

    const handleSubmit = async () => {
        if ($invalid) {
            $batchState({
                $touched: true,
                $dirty: true
            });

            Toast.show($getFirstError());
        } else {
            Toast.loading(true);

            try {
                const params = parseNoticeInfo($params, isIRA, locationState);

                params.type = type.toUpperCase();

                await http.post(API.TRADE.deposit.fillNotice(), params, {
                    useForm: true
                });

                Toast.loading(false);
                await Toast.show(__('汇款通知提交成功！'));

                const tax = {
                    year: $params.tax_year,
                    limit: $params.tax_limit,
                    cost: 0
                };

                if (type === 'check') {
                    navigate('../check-complete', {
                        state: { tax }
                    });
                } else {
                    navigate('../complete', {
                        state: { tax }
                    });
                }
            } catch (error) {
                Toast.loading(false);
                Toast.show(error);
            }
        }
    };

    const renderWireOrCheck = () => {
        if (type === 'wire') {
            return (
                <div className="space-box">
                    <p className="label">{__('汇款凭证')}</p>
                    <UploaderList
                        maxCount={6}
                        type="deposit"
                        name="voucher_pictures"
                        extra={__('请上传清晰的截图或文件，务必包含您的姓名、完整银行账号及该笔入金的详情。')}
                        checker={(value: any) => {
                            if (!value?.[0]?.url) {
                                return __('请上传您的汇款凭证');
                            }

                            return true;
                        }}
                        bucket="customer-deposit"
                    />
                </div>
            );
        }

        if (type === 'check') {
            return (
                <div className="space-box">
                    <p className="label">{__('Upload  the front side of the check')}</p>
                    <UploaderList
                        maxCount={6}
                        type="deposit"
                        name="voucher_pictures_front"
                        bucket="customer-deposit"
                        checker={(value: any) => {
                            if (!value?.[0]?.url) {
                                return __('请上传您的支票凭证');
                            }

                            return true;
                        }}
                    />
                </div>
            );
        }

        return null;
    };

    const render = () => {
        if (isMutatingBankCard || isLoadingChoiceList) {
            return <Loading />;
        }

        if (errorBankCard && !dataBankCard) {
            return <ErrorBox error={errorBankCard} onClick={() => triggerBankCard()} />;
        }

        if (errorChoiceList) {
            return <ErrorBox error={errorChoiceList} onClick={() => mutateChoiceList()} />;
        }

        return (
            <>
                <p className="desc">{__('填写并上传详细转账信息，将有助于我们更快地处理您的入金。')}</p>
                <Form layout="vertical" onFinish={handleSubmit}>
                    <TaxLimit />
                    <div className="space-box">
                        <p className="label">{__('汇款银行账户')}</p>
                        <BankCardSelect
                            filterFiledValue="bankAccount"
                            formItemConfig={{
                                name: 'bank_account',
                                checker: value => {
                                    if (!value) {
                                        return __('请添加银行卡');
                                    }

                                    return true;
                                }
                            }}
                            data={dataBankCard}
                            onAdd={() => {
                                navigate('/profile/bankcards/add', {
                                    state: {
                                        next: '/profile/deposit/notice',
                                        source: 'deposit_notice'
                                    }
                                });
                            }}
                            onEdit={bankcardInfo => {
                                const { id } = bankcardInfo;

                                navigate(`/profile/bankcards/edit/${id}`, {
                                    state: {
                                        source: 'deposit_notice',
                                        next: '/profile/deposit/notice'
                                    }
                                });
                            }}
                        />
                    </div>
                    <div className="space-box">
                        <p className="label">{__('汇款金额')}</p>
                        <CurrencyAmount
                            currencyList={currencyList}
                            inputConfig={{
                                name: 'amount',
                                placeholder: '0.00'
                            }}
                            selectConfig={{
                                name: 'currency',
                                disabled: isIRA || currencyList!.length <= 1,
                                showIcon: !isIRA && currencyList!.length > 1,
                                $defaultValue: isIRA ? 'USD' : locationState?.currency || transferDepositCurrencyList[0]
                            }}
                        />
                    </div>
                    {renderWireOrCheck()}
                    <BottomBox className="btns-box">
                        <Button type="primary" htmlType="submit" className="btn-important">
                            {__('提交并通知老虎')}
                        </Button>
                        <div className="text-center pt-2">
                            <Link to="../record">{__('查看提交记录')}</Link>
                        </div>
                    </BottomBox>
                </Form>
                <Debug data={$params} />
            </>
        );
    };

    return (
        <div className="deposit-fill-notice-root">
            <ScrollTop />
            <h3 className="main-title">{__('上传转账详情')}</h3>
            {render()}
        </div>
    );
};

export default withForm(FillNotice);
